import { useState, useEffect } from "react";
import { useParams, Link, useLocation } from "wouter";
import { getTransactionDetails } from "@/lib/bitcoin-api";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Copy, 
  ExternalLink, 
  Database, 
  Check,
  AlertCircle,
} from "lucide-react";
import { formatDate, formatBitcoin } from "@/lib/utils";

export default function TransactionDetailsPage() {
  const { txid } = useParams<{ txid: string }>();
  const [, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("summary");
  
  // Use React Query to fetch and cache transaction details
  const { 
    data: txDetails, 
    isLoading, 
    isError, 
    error: queryError,
    refetch 
  } = useQuery({
    queryKey: txid ? ['/api/bitcoin/transaction', txid] : ['empty-tx-query'],
    queryFn: () => txid ? getTransactionDetails(txid) : Promise.resolve(null),
    enabled: !!txid,
    staleTime: Infinity, // Transaction data doesn't change
  });

  const handleCopyTxid = () => {
    if (txid) {
      navigator.clipboard.writeText(txid);
      toast({
        title: "Copied to clipboard",
        description: "Transaction ID has been copied to your clipboard.",
      });
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-gray-900">
        {/* Navbar */}
        <nav className="bg-gradient-to-r from-[#A3B7E3] to-[#4A6CA7] border-b border-[#4A6CA7]/30 px-4 py-3 shadow-md">
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center">
              <span className="font-semibold text-xl text-white">Blockseer Explorer</span>
            </div>
            <div>
              <div className="flex items-center space-x-4">
                {user?.username && (
                  <span className="text-sm text-white">{user?.username}</span>
                )}
                <Link href="/">
                  <span className="text-sm text-white hover:text-white/80 transition-colors cursor-pointer">
                    Dashboard
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="flex-grow container mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="animate-pulse">
            <div className="h-10 bg-slate-200 dark:bg-gray-700 rounded mb-4 w-1/4"></div>
            <div className="h-8 bg-slate-200 dark:bg-gray-700 rounded mb-6 w-3/4"></div>
            <div className="h-64 bg-slate-200 dark:bg-gray-700 rounded mb-4"></div>
          </div>
        </div>
      </div>
    );
  }

  if (isError && queryError) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-gray-900">
        {/* Navbar */}
        <nav className="bg-gradient-to-r from-[#A3B7E3] to-[#4A6CA7] border-b border-[#4A6CA7]/30 px-4 py-3 shadow-md">
          <div className="container mx-auto flex justify-between items-center">
            <div className="flex items-center">
              <span className="font-semibold text-xl text-white">Blockseer Explorer</span>
            </div>
            <div>
              <div className="flex items-center space-x-4">
                {user?.username && (
                  <span className="text-sm text-white">{user?.username}</span>
                )}
                <Link href="/">
                  <span className="text-sm text-white hover:text-white/80 transition-colors cursor-pointer">
                    Dashboard
                  </span>
                </Link>
              </div>
            </div>
          </div>
        </nav>

        <div className="flex-grow container mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <div className="bg-white dark:bg-gray-800 shadow-sm dark:shadow-lg dark:shadow-black/20 rounded-lg p-6 border border-slate-200 dark:border-gray-700">
            <div className="text-center py-6">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400 mb-4">
                <AlertCircle className="h-8 w-8" />
              </div>
              <h3 className="text-lg font-medium text-slate-900 dark:text-white mb-2">Error loading transaction</h3>
              <p className="text-slate-600 dark:text-gray-300 mb-4">{queryError instanceof Error ? queryError.message : "Failed to load transaction details"}</p>
              <div className="flex justify-center space-x-4">
                <Button 
                  onClick={() => refetch()}
                  className="dark:text-white dark:bg-blue-700 dark:hover:bg-blue-600"
                >
                  Try Again
                </Button>
                <Button 
                  variant="outline" 
                  onClick={() => setLocation("/")}
                  className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Return to Dashboard
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!txDetails) {
    return (
      <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-gray-900">
        <nav className="bg-gradient-to-r from-[#A3B7E3] to-[#4A6CA7] px-4 py-3 shadow-md">
          <div className="container mx-auto">
            <div className="flex items-center">
              <span className="font-semibold text-xl text-white">Blockseer Explorer</span>
            </div>
          </div>
        </nav>
        <div className="flex-grow container mx-auto py-6 px-4">
          <div className="text-center py-12">
            <h3 className="text-lg font-medium">No transaction data available</h3>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-gray-900">
      {/* Navbar */}
      <nav className="bg-gradient-to-r from-[#A3B7E3] to-[#4A6CA7] border-b border-[#4A6CA7]/30 px-4 py-3 shadow-md">
        <div className="container mx-auto flex justify-between items-center">
          <div className="flex items-center">
            <span className="font-semibold text-xl text-white">Blockseer Explorer</span>
          </div>
          <div>
            <div className="flex items-center space-x-4">
              {user?.username && (
                <span className="text-sm text-white">{user?.username}</span>
              )}
              <Link href="/">
                <span className="text-sm text-white hover:text-white/80 transition-colors cursor-pointer">
                  Dashboard
                </span>
              </Link>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <div className="flex-grow container mx-auto py-6 px-4 sm:px-6 lg:px-8">
        <div className="space-y-6">
          {/* Transaction Header */}
          <div>
            <div className="flex items-center space-x-2 mb-2">
              <span className="bg-[#4A6CA7]/20 text-[#4A6CA7] dark:bg-blue-900/30 dark:text-blue-400 p-1 rounded">
                <Database className="h-5 w-5" />
              </span>
              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Transaction</h1>
            </div>
            <div className="flex items-center space-x-2">
              <span className="font-mono text-sm text-slate-600 dark:text-gray-300">{txid}</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-1 h-7 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white" 
                onClick={handleCopyTxid}
              >
                <Copy className="h-4 w-4" />
              </Button>
              <Button 
                variant="ghost" 
                size="sm" 
                className="p-1 h-7 dark:hover:bg-gray-700 dark:text-gray-300 dark:hover:text-white" 
                asChild
              >
                <a 
                  href={`https://blockstream.info/tx/${txid}`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                >
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>

          {/* Transaction Info Card with Tabs */}
          <Card className="overflow-hidden dark:bg-gray-800 dark:border-gray-700">
            <div className="p-4 border-b dark:border-gray-700 flex justify-between items-center">
              <div>
                {txDetails.status.confirmed ? (
                  <Badge className="bg-green-100 text-green-700 dark:bg-green-900/50 dark:text-green-400 flex items-center gap-1 font-medium border-0">
                    <Check className="h-3.5 w-3.5" /> 
                    <span>Confirmed • 554 confirmations</span>
                  </Badge>
                ) : (
                  <Badge className="bg-amber-100 text-amber-700 dark:bg-amber-900/50 dark:text-amber-400 flex items-center gap-1 font-medium border-0">
                    <AlertCircle className="h-3.5 w-3.5" /> 
                    <span>Unconfirmed</span>
                  </Badge>
                )}
              </div>
              <div className="text-right text-sm text-slate-500 dark:text-gray-400">
                {txDetails.status.block_time ? formatDate(txDetails.status.block_time) : 'Pending'}
              </div>
            </div>
            
            <Tabs defaultValue="summary" className="w-full">
              <div className="border-b dark:border-gray-700">
                <TabsList className="flex bg-[#f0f4ff] dark:bg-gray-800 w-full rounded-none h-12 border-x-0 border-t-0">
                  <TabsTrigger 
                    value="summary" 
                    className="flex-1 rounded-none text-center hover:text-gray-800 dark:hover:text-white data-[state=active]:bg-[#4A6CA7] dark:data-[state=active]:bg-blue-700 data-[state=active]:text-white data-[state=inactive]:bg-[#f0f4ff] dark:data-[state=inactive]:bg-gray-800 dark:data-[state=inactive]:text-gray-300 font-medium"
                  >
                    Summary
                  </TabsTrigger>
                  <TabsTrigger 
                    value="details" 
                    className="flex-1 rounded-none text-center hover:text-gray-800 dark:hover:text-white data-[state=active]:bg-[#4A6CA7] dark:data-[state=active]:bg-blue-700 data-[state=active]:text-white data-[state=inactive]:bg-[#f0f4ff] dark:data-[state=inactive]:bg-gray-800 dark:data-[state=inactive]:text-gray-300 font-medium"
                  >
                    Advanced Details
                  </TabsTrigger>
                </TabsList>
              </div>
              
              <TabsContent value="summary" className="m-0">
                <CardContent className="p-6 dark:bg-gray-800">
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-x-6 gap-y-7">
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Hash</h3>
                      <div className="font-mono text-sm text-slate-800 dark:text-white break-all">
                        {txDetails.txid}
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Block ID</h3>
                      <div className="text-sm text-slate-800 dark:text-white">
                        {txDetails.status.block_hash ? (
                          <Link href={`/?searchType=block_hash&query=${txDetails.status.block_hash}`}>
                            <span className="text-[#0A51A8] dark:text-blue-400 hover:underline cursor-pointer font-medium">
                              {txDetails.status.block_height}
                            </span>
                          </Link>
                        ) : (
                          <span className="text-slate-400 dark:text-gray-500">Pending</span>
                        )}
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Time</h3>
                      <div className="text-sm text-slate-800 dark:text-white">
                        {txDetails.status.block_time ? 
                          formatDate(txDetails.status.block_time) : 
                          'Pending'}
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Age</h3>
                      <div className="text-sm text-slate-800 dark:text-white">
                        {txDetails.status.block_time ? 
                          `${Math.floor((Date.now()/1000 - txDetails.status.block_time)/60/60)} h ${Math.floor(((Date.now()/1000 - txDetails.status.block_time)/60) % 60)} m` : 
                          'Pending'}
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Inputs</h3>
                      <div className="text-sm font-medium text-slate-800 dark:text-white">
                        {txDetails.vin.length}
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Outputs</h3>
                      <div className="text-sm font-medium text-slate-800 dark:text-white">
                        {txDetails.vout.length}
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Output Value</h3>
                      <div className="text-sm font-semibold text-slate-800 dark:text-white">
                        {formatBitcoin(txDetails.vout.reduce((sum, output) => sum + output.value, 0))} BTC
                      </div>
                    </div>
                    
                    <div className="px-4 py-3 rounded-md dark:bg-gray-750 border border-transparent dark:border-gray-700">
                      <h3 className="text-sm font-semibold text-slate-500 dark:text-gray-300 mb-2">Fee</h3>
                      <div className="text-sm font-medium text-slate-800 dark:text-white">
                        {formatBitcoin(txDetails.fee)} BTC
                      </div>
                    </div>
                  </div>
                  
                  {/* From/To Section */}
                  <div className="mt-6 pt-6 border-t border-slate-200 dark:border-gray-700">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-base font-semibold text-slate-700 dark:text-white mb-3">From</h3>
                        <div className="space-y-3">
                          {txDetails.vin.some(input => input.is_coinbase) ? (
                            <div className="bg-slate-50 dark:bg-gray-750 p-4 rounded-md border border-slate-200 dark:border-gray-600 shadow-sm">
                              <span className="text-sm font-medium dark:text-gray-200">Block Reward</span>
                              <span className="ml-2 text-xs text-slate-500 dark:text-gray-400">Coinbase</span>
                            </div>
                          ) : (
                            txDetails.vin.map((input, index) => (
                              <div key={`input-${index}`} className="bg-slate-50 dark:bg-gray-750 p-4 rounded-md border border-slate-200 dark:border-gray-600 shadow-sm">
                                <div className="flex items-center">
                                  <div className="h-6 w-6 rounded-full bg-blue-100 dark:bg-blue-800 flex items-center justify-center text-blue-600 dark:text-blue-300 mr-3 font-semibold">
                                    {index + 1}
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    {input.prevout?.scriptpubkey_address ? (
                                      <Link href={`/?searchType=address&query=${input.prevout.scriptpubkey_address}`}>
                                        <span className="text-sm font-medium text-[#0A51A8] dark:text-blue-300 hover:underline dark:hover:text-blue-200 truncate block cursor-pointer">
                                          {input.prevout.scriptpubkey_address}
                                        </span>
                                      </Link>
                                    ) : (
                                      <span className="text-sm font-medium text-slate-500 dark:text-gray-400 italic">Unknown</span>
                                    )}
                                  </div>
                                  {input.prevout?.value !== undefined && (
                                    <div className="text-right whitespace-nowrap ml-2">
                                      <div className="text-sm font-medium text-slate-700 dark:text-white">
                                        {formatBitcoin(input.prevout.value)} BTC
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            ))
                          )}
                        </div>
                      </div>
                      
                      <div>
                        <h3 className="text-base font-semibold text-slate-700 dark:text-white mb-3">To</h3>
                        <div className="space-y-3">
                          {txDetails.vout.map((output, index) => (
                            <div key={`output-${index}`} className="bg-slate-50 dark:bg-gray-750 p-4 rounded-md border border-slate-200 dark:border-gray-600 shadow-sm">
                              <div className="flex items-center">
                                <div className="h-6 w-6 rounded-full bg-green-100 dark:bg-green-800 flex items-center justify-center text-green-600 dark:text-green-300 mr-3 font-semibold">
                                  {index + 1}
                                </div>
                                <div className="flex-1 min-w-0">
                                  {output.scriptpubkey_address ? (
                                    <Link href={`/?searchType=address&query=${output.scriptpubkey_address}`}>
                                      <span className="text-sm font-medium text-[#0A51A8] dark:text-blue-300 hover:underline dark:hover:text-blue-200 truncate block cursor-pointer">
                                        {output.scriptpubkey_address}
                                      </span>
                                    </Link>
                                  ) : (
                                    <span className="text-sm font-medium text-slate-600 dark:text-gray-300 italic">
                                      {output.scriptpubkey_type === 'op_return' ? 'OP_RETURN' : 'Unable to decode'}
                                    </span>
                                  )}
                                </div>
                                <div className="text-right whitespace-nowrap ml-2">
                                  <div className="text-sm font-medium text-slate-700 dark:text-white">
                                    {formatBitcoin(output.value)} BTC
                                  </div>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </TabsContent>
              
              <TabsContent value="details" className="m-0">
                <CardContent className="p-6">
                  <div className="grid grid-cols-2 md:grid-cols-2 gap-6">
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Hash</h3>
                      <div className="font-mono text-sm text-slate-800 break-all">
                        {txDetails.txid}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Block ID</h3>
                      <div className="font-mono text-sm text-slate-800 break-all">
                        {txDetails.status.block_hash || 'Pending'}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Time</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.status.block_time ? 
                          formatDate(txDetails.status.block_time) : 
                          'Pending'}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Age</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.status.block_time ? 
                          `${Math.floor((Date.now()/1000 - txDetails.status.block_time)/60/60)} h ${Math.floor(((Date.now()/1000 - txDetails.status.block_time)/60) % 60)} m` : 
                          'Pending'}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Inputs</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.vin.length}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Outputs</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.vout.length}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Output Value</h3>
                      <div className="text-sm text-slate-800">
                        {formatBitcoin(txDetails.vout.reduce((sum, output) => sum + output.value, 0))} BTC
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Fee</h3>
                      <div className="text-sm text-slate-800">
                        {formatBitcoin(txDetails.fee)} BTC
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Fee/vB</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.fee && txDetails.size 
                          ? `${Math.round(txDetails.fee / txDetails.size * 100000000)} sat/byte` 
                          : 'N/A'}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Weight</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.weight || 0}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Weight Unit</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.weight ? (txDetails.weight / 4) : 0}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Witness</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.weight && txDetails.size ? (txDetails.weight > txDetails.size * 4 ? "Yes" : "No") : "No"}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Locktime</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.locktime || 0}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">BTC Price</h3>
                      <div className="text-sm text-slate-800">
                        $93,891.31
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Coinbase</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.vin.some(input => input.is_coinbase) ? "Yes" : "No"}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">RBF</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.vin.some(input => input.sequence < 0xffffffff - 1) ? "Yes" : "No"}
                      </div>
                    </div>
                    
                    <div>
                      <h3 className="text-sm font-medium text-slate-500 mb-2">Version</h3>
                      <div className="text-sm text-slate-800">
                        {txDetails.version || 1}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </TabsContent>
            </Tabs>
          </Card>
          
          {/* Bottom Action Buttons */}
          <div className="flex justify-end space-x-3">
            <Button variant="outline" size="sm" onClick={handleCopyTxid}>
              <Copy className="h-4 w-4 mr-2" /> Copy TXID
            </Button>
            <Button variant="outline" size="sm" asChild>
              <a 
                href={`https://blockstream.info/tx/${txid}`} 
                target="_blank" 
                rel="noopener noreferrer"
              >
                <ExternalLink className="h-4 w-4 mr-2" /> View on Blockstream
              </a>
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}